import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import axios from 'axios';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../utils';
import { defaultFilterDates, formatDate } from '../../utils/functions';
import Add from './add';
import { useSelector } from 'react-redux';

const PER_PAGE = 10;

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const getSortValue = (item, key) => {
  if (key === 'product') return item.variant?.name ?? '';
  if (key === 'quantity') return Number(item.quantity) || 0;
  if (key === 'reason') return item.reason ?? '';
  if (key === 'created_at') return item.created_at ?? '';
  return item[key] ?? '';
};

export default function StockAdjustments() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => ({ search: '', ...defaultFilterDates() }));
  const [draftFilters, setDraftFilters] = useState(() => defaultFilterDates());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/stock-adjustments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : response.data?.data ?? [];
      setData(list);
    } catch (error) {
      console.error('Erreur lors de la récupération des ajustements :', error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredItems = useMemo(() => {
    return data.filter((item) => {
      const search = appliedFilters.search.trim().toLowerCase();
      const name = (item.variant?.name || '').toLowerCase();
      if (search && !name.includes(search)) return false;

      if (appliedFilters.dateFrom && item.created_at) {
        if (new Date(item.created_at) < new Date(appliedFilters.dateFrom)) return false;
      }
      if (appliedFilters.dateTo && item.created_at) {
        const end = new Date(appliedFilters.dateTo);
        end.setHours(23, 59, 59, 999);
        if (new Date(item.created_at) > end) return false;
      }
      return true;
    });
  }, [data, appliedFilters]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, key);
      const bValue = getSortValue(b, key);
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * dir;
      }
      const aStr = String(aValue);
      const bStr = String(bValue);
      if (aStr < bStr) return -1 * dir;
      if (aStr > bStr) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  const stats = useMemo(
    () => ({
      total: filteredItems.length,
      qtySum: filteredItems.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0),
    }),
    [filteredItems]
  );

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PER_PAGE));
  const indexOfLast = currentPage * PER_PAGE;
  const indexOfFirst = indexOfLast - PER_PAGE;
  const currentItems = sortedItems.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters(defaultFilterDates());
    setAppliedFilters({ search: '', ...defaultFilterDates() });
    setCurrentPage(1);
    setSortConfig({ key: 'created_at', direction: 'desc' });
  };

  const handleRefresh = async () => {
    await fetchData();
    setCurrentPage(1);
  };

  const exportRows = (items) =>
    items.map((item) => ({
      Produit: item.variant?.name ?? '—',
      Quantité: item.quantity,
      Motif: item.reason,
      'Créée le': formatDate(item.created_at),
    }));

  const exportToPDF = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const doc = new jsPDF();
    doc.text('Ajustements de stock', 14, 16);
    doc.autoTable({
      head: [['Produit', 'Quantité', 'Motif', 'Créée le']],
      body: rows.map((r) => [r.Produit, r.Quantité, r.Motif, r['Créée le']]),
      startY: 24,
      headStyles: { fillColor: [88, 204, 2] },
    });
    doc.save(`Inventaire_${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const worksheet = utils.json_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Inventaire');
    writeFile(workbook, `Inventaire_${APP_NAME}.xlsx`);
  };

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Administration</p>
          <h1 className="duo-hero-title">Ajustements de stock</h1>
          <p className="duo-hero-sub mt-2 mb-0">Inventaires, casses et corrections de stock.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Ajustements (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Quantité totale</p>
            <p className="duo-kpi-chip__value">{stats.qtySum}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des ajustements
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={() => window.print()} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <button type="button" className="duo-btn duo-btn--primary" onClick={() => setShowAddModal(true)}>
                  <i className="bi bi-plus" /> Nouveau
                </button>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher un produit..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <div>
                    <label className="duo-form-label">Date de début</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.dateFrom}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Date de fin</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.dateTo}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          {(appliedFilters.dateFrom || appliedFilters.dateTo) && (
            <div className="px-3 py-2 border-bottom small text-muted" style={{ borderColor: '#f0f0f0' }}>
              Période : {appliedFilters.dateFrom || '…'} → {appliedFilters.dateTo || '…'}
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('product')}>
                    Produit <SortIcon active={isSortActive('product')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('quantity')}>
                    Quantité <SortIcon active={isSortActive('quantity')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('reason')}>
                    Motif <SortIcon active={isSortActive('reason')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('created_at')}>
                    Créée le <SortIcon active={isSortActive('created_at')} direction={sortConfig.direction} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-bold">{item.variant?.name ?? '—'}</td>
                      <td>{item.quantity}</td>
                      <td>{item.reason}</td>
                      <td className="text-nowrap">{formatDate(item.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="duo-empty-row">
                      Aucun ajustement correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      <Add open={showAddModal} onClose={() => setShowAddModal(false)} refreshData={handleRefresh} />
    </MainLayout>
  );
}
